import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Save, RotateCcw, Crown, Star, User, Bot } from 'lucide-react';
import api from '../lib/api';

// Available LLM providers and their models
const LLM_OPTIONS = {
  mistral: {
    name: 'Mistral',
    models: ['mistral-small-latest', 'mistral-medium-latest', 'mistral-large-latest']
  },
  claude: {
    name: 'Claude',
    models: ['claude-sonnet-4-20250514', 'claude-3-haiku-20240307', 'claude-3-opus-20240229']
  },
  gemini: {
    name: 'Gemini',
    models: ['gemini-2.0-flash', 'gemini-1.5-pro', 'gemini-1.5-flash']
  },
  openai: {
    name: 'OpenAI',
    models: ['gpt-4o', 'gpt-4o-mini', 'gpt-4-turbo']
  },
  ollama: {
    name: 'Ollama (Self-hosted)',
    models: ['llama3', 'mistral', 'codellama', 'phi3']
  }
};

export default function AdminSubscriptionsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [editedConfigs, setEditedConfigs] = useState({});

  useEffect(() => {
    if (user?.admin_role !== 'super_admin') {
      navigate('/admin');
      return;
    }
    fetchConfigs();
  }, [user, navigate]);

  const fetchConfigs = async () => {
    try {
      const response = await api.get('/admin/subscription-config');
      const configsMap = {};
      response.data.configs.forEach(c => {
        configsMap[c.subscription_type] = c;
      });
      setConfigs(response.data.configs);
      setEditedConfigs(configsMap);
    } catch (error) {
      console.error('Error fetching configs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (subType, field, value) => {
    setEditedConfigs(prev => ({
      ...prev,
      [subType]: {
        ...prev[subType],
        [field]: value
      }
    }));
  };

  const handleSave = async (subType) => {
    setSaving(subType);
    try {
      await api.put(`/admin/subscription-config/${subType}`, editedConfigs[subType]);
      alert(`Configuration ${subType} enregistrée !`);
    } catch (error) {
      alert(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(null);
    }
  };

  const handleReset = async () => {
    if (!confirm('Réinitialiser toutes les configurations aux valeurs par défaut ?')) return;
    try {
      await api.post('/admin/subscription-config/reset');
      fetchConfigs();
      alert('Configurations réinitialisées !');
    } catch (error) {
      console.error('Error resetting:', error);
    }
  };

  const getIcon = (subType) => {
    switch (subType) {
      case 'premium': return <Crown className="w-6 h-6 text-yellow-500" />;
      case 'standard': return <Star className="w-6 h-6 text-blue-500" />;
      default: return <User className="w-6 h-6 text-gray-500" />;
    }
  };

  const getColor = (subType) => {
    switch (subType) {
      case 'premium': return 'border-yellow-500';
      case 'standard': return 'border-blue-500';
      default: return 'border-gray-300';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-card border-b border-border sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/admin')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-xl font-bold">Configuration Abonnements</h1>
          </div>
          <Button variant="outline" onClick={handleReset}>
            <RotateCcw className="w-4 h-4 mr-2" />
            Réinitialiser
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid md:grid-cols-3 gap-6">
          {['free', 'standard', 'premium'].map((subType) => {
            const config = editedConfigs[subType] || {};
            return (
              <Card key={subType} className={`border-2 ${getColor(subType)}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-3">
                    {getIcon(subType)}
                    <CardTitle className="capitalize">{config.name || subType}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Name & Price */}
                  <div>
                    <label className="text-sm font-medium">Nom affiché</label>
                    <Input
                      value={config.name || ''}
                      onChange={(e) => handleChange(subType, 'name', e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-sm font-medium">Prix (€)</label>
                      <Input
                        type="number"
                        step="0.01"
                        value={config.price || 0}
                        onChange={(e) => handleChange(subType, 'price', parseFloat(e.target.value) || 0)}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Affichage prix</label>
                      <Input
                        value={config.price_display || ''}
                        onChange={(e) => handleChange(subType, 'price_display', e.target.value)}
                        placeholder="6,99€/mois"
                        className="mt-1"
                      />
                    </div>
                  </div>

                  {/* Limits */}
                  <div className="pt-2 border-t">
                    <p className="text-sm font-medium mb-2">Limites mensuelles</p>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm">Questions IA</span>
                        <Input
                          type="number"
                          value={config.ai_questions_per_month ?? 0}
                          onChange={(e) => handleChange(subType, 'ai_questions_per_month', parseInt(e.target.value) || 0)}
                          className="w-24 h-8 text-sm"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm">Scans OCR</span>
                        <Input
                          type="number"
                          value={config.ocr_scans_per_month ?? 0}
                          onChange={(e) => handleChange(subType, 'ocr_scans_per_month', parseInt(e.target.value) || 0)}
                          className="w-24 h-8 text-sm"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm">Factures stockées</span>
                        <Input
                          type="number"
                          value={config.invoice_limit ?? 0}
                          onChange={(e) => handleChange(subType, 'invoice_limit', parseInt(e.target.value) || 0)}
                          className="w-24 h-8 text-sm"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm">Abonnements suivis</span>
                        <Input
                          type="number"
                          value={config.recurring_subscriptions_limit ?? 0}
                          onChange={(e) => handleChange(subType, 'recurring_subscriptions_limit', parseInt(e.target.value) || 0)}
                          className="w-24 h-8 text-sm"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm">Transactions/mois</span>
                        <Input
                          type="number"
                          value={config.transactions_per_month ?? 0}
                          onChange={(e) => handleChange(subType, 'transactions_per_month', parseInt(e.target.value) || 0)}
                          className="w-24 h-8 text-sm"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm">Membres famille</span>
                        <Input
                          type="number"
                          value={config.family_members_max ?? 0}
                          onChange={(e) => handleChange(subType, 'family_members_max', parseInt(e.target.value) || 0)}
                          className="w-24 h-8 text-sm"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm">Historique rapports (mois)</span>
                        <Input
                          type="number"
                          value={config.reports_months_history ?? 0}
                          onChange={(e) => handleChange(subType, 'reports_months_history', parseInt(e.target.value) || 0)}
                          className="w-24 h-8 text-sm"
                        />
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">-1 = illimité, 0 = désactivé</p>
                  </div>

                  {/* Features */}
                  <div className="pt-2 border-t">
                    <p className="text-sm font-medium mb-2">Fonctionnalités</p>
                    <div className="space-y-2">
                      {[
                        { key: 'voice_ai', label: 'Assistant vocal IA' },
                        { key: 'predictions', label: 'Prédictions IA' },
                        { key: 'pdf_reports', label: 'Rapports PDF' },
                        { key: 'full_export', label: 'Export complet' },
                        { key: 'expense_simulator', label: 'Simulateur de dépense' },
                      ].map(({ key, label }) => (
                        <div key={key} className="flex items-center justify-between">
                          <span className="text-sm">{label}</span>
                          <Switch
                            checked={config[key] || false}
                            onCheckedChange={(checked) => handleChange(subType, key, checked)}
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* LLM Configuration */}
                  <div className="pt-2 border-t">
                    <div className="flex items-center gap-2 mb-2">
                      <Bot className="w-4 h-4 text-purple-500" />
                      <p className="text-sm font-medium">Configuration IA</p>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <label className="text-xs text-muted-foreground">Fournisseur LLM</label>
                        <Select
                          value={config.llm_provider || 'none'}
                          onValueChange={(value) => {
                            handleChange(subType, 'llm_provider', value === 'none' ? null : value);
                            // Reset model when provider changes
                            if (value !== 'none') {
                              const defaultModel = LLM_OPTIONS[value]?.models[0];
                              handleChange(subType, 'llm_model', defaultModel);
                            } else {
                              handleChange(subType, 'llm_model', null);
                            }
                          }}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue placeholder="Sélectionner..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">❌ Aucun (désactivé)</SelectItem>
                            {Object.entries(LLM_OPTIONS).map(([key, { name }]) => (
                              <SelectItem key={key} value={key}>
                                {key === 'mistral' && '🔶'} 
                                {key === 'claude' && '🟣'} 
                                {key === 'gemini' && '🔵'} 
                                {key === 'openai' && '🟢'} 
                                {key === 'ollama' && '🖥️'} 
                                {' '}{name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {config.llm_provider && LLM_OPTIONS[config.llm_provider] && (
                        <div>
                          <label className="text-xs text-muted-foreground">Modèle</label>
                          <Select
                            value={config.llm_model || LLM_OPTIONS[config.llm_provider].models[0]}
                            onValueChange={(value) => handleChange(subType, 'llm_model', value)}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {LLM_OPTIONS[config.llm_provider].models.map((model) => (
                                <SelectItem key={model} value={model}>
                                  {model}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                      
                      {!config.llm_provider && (
                        <p className="text-xs text-amber-600 bg-amber-50 dark:bg-amber-900/20 p-2 rounded">
                          ⚠️ L'assistant IA sera désactivé pour cet abonnement
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Save Button */}
                  <Button
                    className="w-full mt-4"
                    onClick={() => handleSave(subType)}
                    disabled={saving === subType}
                  >
                    {saving === subType ? (
                      <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    Enregistrer
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </main>
    </div>
  );
}
